import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import { enUS } from "react-day-picker/locale";
import {
  isoDateToLocalDate,
  localDateToIsoDate,
  parseIsoDate,
  isIsoDateAfter,
} from "@/utils/format/isoDate";
import { cn } from "@/utils/cn";

const inputClass =
  "filter-date-input h-8 min-w-0 flex-1 rounded-l-md border border-[var(--border)] border-r-0 bg-white px-2 py-1 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[rgba(15,118,110,0.12)] placeholder:text-[var(--muted)]";

interface IsoDateInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  "aria-label": string;
  className?: string;
  /** Disable dates after this day (local calendar). */
  maxDate?: Date;
}

function CalendarIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

/**
 * English date field: text (YYYY-MM-DD) + calendar popover (en-US, not OS locale).
 * Popover renders in a portal with fixed positioning so it overlays content below.
 */
export function IsoDateInput({
  id,
  value,
  onChange,
  "aria-label": ariaLabel,
  className,
  maxDate,
}: IsoDateInputProps) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const popoverId = `${inputId}-popover`;
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  useEffect(() => {
    setText(value);
  }, [value]);

  const updatePopoverPosition = () => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const popupWidth = popoverRef.current?.offsetWidth ?? 320;
    const popupHeight = popoverRef.current?.offsetHeight ?? 340;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gap = 4;
    const edge = 8;

    const left = Math.min(
      Math.max(edge, rect.left),
      Math.max(edge, viewportWidth - popupWidth - edge),
    );

    const placeAbove =
      rect.bottom + gap + popupHeight > viewportHeight - edge &&
      rect.top - gap - popupHeight >= edge;

    const top = placeAbove ? rect.top - gap - popupHeight : rect.bottom + gap;

    setPopoverStyle({
      top: Math.max(edge, top),
      left,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePopoverPosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        rootRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    const onReposition = () => updatePopoverPosition();

    document.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);

    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open]);

  const maxIso = maxDate ? localDateToIsoDate(maxDate) : undefined;

  const commitText = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      onChange("");
      setText("");
      return;
    }
    const parsed = parseIsoDate(trimmed);
    if (parsed) {
      const next =
        maxIso && isIsoDateAfter(parsed, maxIso) ? maxIso : parsed;
      onChange(next);
      setText(next);
      return;
    }
    setText(value);
  };

  const pickDate = (date: Date | undefined) => {
    if (!date) {
      return;
    }
    let iso = localDateToIsoDate(date);
    if (maxIso && isIsoDateAfter(iso, maxIso)) {
      iso = maxIso;
    }
    onChange(iso);
    setText(iso);
    setOpen(false);
  };

  const selected = isoDateToLocalDate(value);
  const today = maxDate ?? new Date();
  const pickerMonth =
    selected && maxDate && selected.getTime() > maxDate.getTime()
      ? maxDate
      : (selected ?? maxDate ?? new Date());

  const popover = open ? (
    <div
      id={popoverId}
      ref={popoverRef}
      role="dialog"
      aria-label={ariaLabel}
      className="invoice-date-picker-popup fixed z-[120] rounded-lg border border-[var(--border-soft)] bg-white p-2 shadow-[0_6px_16px_rgba(0,0,0,0.08)]"
      style={{ top: popoverStyle.top, left: popoverStyle.left }}
    >
      <DayPicker
        mode="single"
        locale={enUS}
        selected={selected}
        onSelect={pickDate}
        defaultMonth={pickerMonth}
        endMonth={maxDate}
        disabled={maxDate ? { after: maxDate } : undefined}
      />
      <div className="mt-1 flex justify-between gap-2 border-t border-[var(--border-soft)] pt-2">
        <button
          type="button"
          className="text-xs font-semibold text-[var(--muted)] hover:text-[var(--text)]"
          onClick={() => {
            onChange("");
            setText("");
            setOpen(false);
          }}
        >
          Clear
        </button>
        <button
          type="button"
          className="text-xs font-semibold text-[var(--accent)] hover:underline"
          onClick={() => pickDate(today)}
        >
          Today
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div ref={rootRef} className={cn("relative min-w-0", className)}>
      <div className="flex min-w-0">
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder="YYYY-MM-DD"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={open ? popoverId : undefined}
          title="Format: YYYY-MM-DD"
          className={inputClass}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitText();
              (e.target as HTMLInputElement).blur();
            }
            if (e.key === "Escape") {
              setOpen(false);
            }
          }}
        />
        <button
          type="button"
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-r-md border border-[var(--border)] bg-white text-[var(--muted)]",
            "hover:border-[var(--accent)] hover:text-[var(--accent)]",
            open &&
              "border-[var(--accent)] text-[var(--accent)] ring-2 ring-[rgba(15,118,110,0.12)]",
          )}
          aria-label={`${ariaLabel}, open calendar`}
          aria-expanded={open}
          aria-controls={open ? popoverId : undefined}
          onClick={() => setOpen((o) => !o)}
        >
          <CalendarIcon />
        </button>
      </div>

      {popover ? createPortal(popover, document.body) : null}
    </div>
  );
}
